import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { useEffect, useRef, useState, useContext } from 'react';
import Card from '../../components/Card/Card';
import WatchGuideLink from '../../components/WatchGuideLink/WatchGuideLink';
import { categories } from '../../utils/cardInfo';
import { Context } from '../../AuthContext';
import { useCategory } from '../../CategoryContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

const BookmarksPage = () => {
  const { user } = useContext(Context);
  const navigate = useNavigate();
  const currentUrl = window.location.pathname;
  const { vertical_id } = useParams();
  const { verticals } = useCategory();

  const [loading, setLoading] = useState(true);
  const [activeVerticals, setActiveVerticals] = useState({});
  const [list, setList] = useState({});
  const [activeFilters, setActiveFilters] = useState(
    Object.fromEntries(
      [
        'papers',
        'patents',
        'grants',
        'people',
        'authors',
        'inventors',
        'jobs',
        'organizations',
        'events',
        'tutorials',
        'users',
      ].map((key) => [key, true])
    )
  );
  const location = useLocation();
  const new_card_id = location.state?.new_card_id || {};
  let animationRef = useRef(JSON.parse(JSON.stringify(location.state?.animation || {})));
  let animationRefLink = useRef(false);

  const filters = [
    {
      icon: <img src={`${process.env.PUBLIC_URL}/categoryIcons/publications.svg`} />,
      text: 'Publications',
      path: 'paper',
      key: 'papers',
    },
    {
      icon: <img src={`${process.env.PUBLIC_URL}/categoryIcons/patents.svg`} />,
      text: 'Patents',
      key: 'patents',
    },
    {
      icon: <img src={`${process.env.PUBLIC_URL}/categoryIcons/funding.svg`} />,
      text: 'Funding',
      key: 'grants',
    },
    {
      icon: <img src={`${process.env.PUBLIC_URL}/categoryIcons/people.svg`} />,
      text: 'People',
      key: 'people',
    },
    {
      icon: <img src={`${process.env.PUBLIC_URL}/categoryIcons/jobs.svg`} />,
      text: 'Jobs',
      key: 'jobs',
    },
    {
      icon: <img src={`${process.env.PUBLIC_URL}/categoryIcons/organizations.svg`} />,
      text: 'Organizations',
      key: 'organizations',
    },
    {
      icon: <img src={`${process.env.PUBLIC_URL}/categoryIcons/events.svg`} />,
      text: 'Events',
      key: 'events',
    },
    {
      icon: <img src={`${process.env.PUBLIC_URL}/categoryIcons/tutorials.svg`} />,
      text: 'Tutorials',
      key: 'tutorials',
    },
  ];

  // Handle checkbox change
  const handleFilterChange = (category) => {
    if (category === 'people') {
      setActiveFilters((prevFilters) => ({
        ...prevFilters,
        authors: !prevFilters[category],
        people: !prevFilters[category],
        inventors: !prevFilters[category],
        users: !prevFilters[category],
      }));
    } else {
      setActiveFilters((prevFilters) => ({
        ...prevFilters,
        [category]: !prevFilters[category],
      }));
    }
  };

  // Reset filters to default (all true)
  const resetFilters = () => {
    setActiveFilters({
      papers: true,
      patents: true,
      grants: true,
      people: true,
      authors: true,
      inventors: true,
      jobs: true,
      organizations: true,
      events: true,
      tutorials: true,
      users: true,
    });
  };

  const resetVerticals = () => {
    setActiveVerticals(
      filteredVerticals.reduce((acc, v) => ({ ...acc, [v.id]: v.id === vertical_id }), {})
    );
  };

  // Handle vertical filter toggle
  const handleVerticalChange = (id) => {
    setActiveVerticals((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleBookmarkToggle = async (event, category, itemId, vertical_id) => {
    event.stopPropagation();

    setList((prevList) => ({
      ...prevList,
      [category]: prevList[category].filter(
        (item) => !(item._id === itemId && item.vertical_id === vertical_id)
      ),
    }));

    try {
      const url = `${process.env.REACT_APP_FLASK_WEBSERVER}toggle_bookmark`;
      const token = await user.getIdToken();
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: user.uid,
          item_id: itemId,
          category,
          vertical_id,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Failed to toggle:', error);
    }
  };

  const fetchBookmarks = async () => {
    try {
      const token = await user.getIdToken();
      const url = `${process.env.REACT_APP_FLASK_WEBSERVER}get_all_bookmarks?vertical_id=${vertical_id}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 403) {
        navigate('/forbidden', { replace: true });
        return {};
      }

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      return result.bookmarks || {}; // Ensure we return an empty object if no bookmarks exist
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
      return {}; // Return empty to avoid undefined issues
    }
  };

  const fetchAndSetBookmarks = async () => {
    setLoading(true);
    const bookmarks = await fetchBookmarks();
    setList(bookmarks);
    setLoading(false);
  };

  useEffect(() => {
    document.title = 'My Collection - The Simple Platform';
    if (!user) return;
    fetchAndSetBookmarks();
  }, [user]);

  useEffect(() => {
    if (verticals.length > 0) {
      let filteredVerticals;
      filteredVerticals = verticals.filter((v) => v.status === 'Joined' || v.status === 'Created');

      const id = vertical_id;

      setActiveVerticals(
        filteredVerticals.reduce((acc, v) => ({ ...acc, [v.id]: v.id === id }), {})
      );
    }
  }, [verticals, currentUrl]); // Runs only when `verticals` updates

  const displayAnimation = (id) => {
    if (id == new_card_id && animationRef.current) {
      animationRef.current = false;
      return true;
    }
    return false;
  };

  const displayAnimationLink = () => {
    if (animationRefLink.current) {
      animationRefLink.current = false;
      return true;
    }
    return false;
  };

  let filteredVerticals = verticals.filter((v) => v.status === 'Joined' || v.status === 'Created');

  // Flatten, filter and sort all items into one array
  const allSortedItems = Object.entries(list)
    .filter(([key]) => activeFilters[key])
    .flatMap(([category, items]) =>
      (Array.isArray(items) ? items : [])
        .filter((item) => activeVerticals?.[item.vertical_id])
        .map((item) => ({ ...item, _category: category }))
    )
    .sort((a, b) => new Date(b.bookmark_created_at || 0) - new Date(a.bookmark_created_at || 0));

  if (loading) {
    return (
      <div className="container">
        <FontAwesomeIcon icon={faSpinner} spin size="10x" color="white"></FontAwesomeIcon>
      </div>
    );
  }

  return (
    <div style={{ width: '70%' }}>
      <div className="papers">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: 0 }}>
            <h3 style={{ margin: 0 }}>My Collection</h3>
            <WatchGuideLink videoId="bookmarking-resources" label="Watch Guide: My Collection" />
          </div>
          <p style={{ marginTop: 0, paddingTop: 0, fontSize: '12px' }}>
            {allSortedItems.length}&nbsp;results
          </p>

          <div style={{ display: 'flex', gap: 50 }}>
            <div style={{ width: '90%' }} className="papers">
              {allSortedItems.map((item, index) => (
                <Card
                  key={item['_id']}
                  category={item._category}
                  details={item}
                  editor={false}
                  cardHeader={categories[item._category]?.cardHeader || item?.type.toUpperCase()}
                  icons={categories[item._category]?.icons}
                  urlIcons={categories[item._category]?.urlIcons}
                  dotColor={categories[item._category]?.dotColor}
                  vertical_id={item.vertical_id}
                  updateNumResults={() => {}}
                  animation={(index == 0 && displayAnimationLink()) || displayAnimation(item._id)}
                  handleBookmarkToggle={(event) => {
                    handleBookmarkToggle(event, item._category, item._id, item.vertical_id);
                  }}
                  uid={user.uid}
                  verticalName={filteredVerticals
                    .find((v) => v.id === item.vertical_id)
                    ?.name?.toUpperCase()}
                />
              ))}
            </div>

            {/* Filters */}
            <div
              style={{
                boxShadow: '0px 4px 4px 0px #00000040',
                backgroundColor: '#FFFFFF',
                position: 'fixed',
                right: 0,
                height: '100%',
                top: 0,
                maxWidth: '250px',
              }}
            >
              <div
                style={{ marginTop: '80px', maxHeight: '80%', overflowY: 'auto', padding: '20px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <p
                    style={{
                      color: '#1C1B1F',
                      textTransform: 'uppercase',
                      fontWeight: '600',
                      fontSize: '12px',
                    }}
                  >
                    Category
                  </p>
                  <button
                    onClick={resetFilters}
                    style={{
                      backgroundColor: 'inherit',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#596274',
                      fontSize: '12px',
                    }}
                  >
                    Reset
                  </button>
                </div>

                <FormGroup>
                  {filters.map(({ text, icon, key }) => (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '10px', marginTop: '7px' }}>{icon}</span>
                      <FormControlLabel
                        key={key}
                        control={
                          <Checkbox
                            checked={activeFilters[key]}
                            onChange={() => handleFilterChange(key)}
                          />
                        }
                        label={text}
                      />
                    </div>
                  ))}
                </FormGroup>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <p
                    style={{
                      color: '#1C1B1F',
                      textTransform: 'uppercase',
                      fontWeight: '600',
                      fontSize: '12px',
                    }}
                  >
                    Vertical
                  </p>
                  <button
                    onClick={resetVerticals}
                    style={{
                      backgroundColor: 'inherit',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#596274',
                      fontSize: '12px',
                    }}
                  >
                    Reset
                  </button>
                </div>

                <FormGroup sx={{ marginBottom: '40px' }}>
                  {filteredVerticals.map(({ id, name }) => (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        borderBottom: '2px solid rgb(228, 231, 233)',
                      }}
                    >
                      <FormControlLabel
                        key={id}
                        control={
                          <Checkbox
                            checked={activeVerticals[id]}
                            onChange={() => handleVerticalChange(id)}
                          />
                        }
                        label={name}
                      />
                    </div>
                  ))}
                </FormGroup>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookmarksPage;
